<script setup lang="ts">
import type { SetupIntentResult } from '@stripe/stripe-js';
import { useQueryClient } from '@tanstack/vue-query';
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';

import { apiGetSetupIntent, getApiGetPaymentMethodsQueryKey } from '~/apiClient';
import { NotificationDuration } from '~/types/notifications.type';

// PROPS
defineProps({
    label: { type: String, default: undefined },
});

// COMPOSABLES
const breakpoints = useBreakpoints(breakpointsTailwind);
const { tt } = useTypedI18n();
const { notifyError } = useNotification();
const { isPaymentElementReady, isPaymentElementLoaderStart, isConfirmingSetup, mountElements, mountPayment, destroy, confirmSetup } = useStripe();
const { logWarn } = useLogs();
const { data } = useAuthUtils();
const queryClient = useQueryClient();

// DATA
const open = ref(false);
const fullscreen = breakpoints.smaller('md');
const errors = ref<string[]>([]);

// HANDLERS
const onError = (): void => {
    notifyError({
        title: tt('notifications.genericError'),
        description: tt('paymentMethods.addModal.error'),
        duration: NotificationDuration.Long,
    });
    open.value = false;
};

const resetErrors = (): any => (errors.value = []);

const onSubmit = (): void => {
    confirmSetup()
        .then(({ setupIntent, error }: SetupIntentResult) => {
            if (setupIntent) {
                // Success
                open.value = false;
                queryClient.invalidateQueries({ queryKey: getApiGetPaymentMethodsQueryKey() });
            } else if (error) {
                if ((error.type === 'card_error' || error.type === 'validation_error') && error.message) {
                    errors.value.push(error?.message);
                } else {
                    errors.value.push(tt('notifications.genericError'));
                }
            }
        })
        .catch(({ code }) => {
            switch (code) {
                case StripeErrorCodes.ELEMENTS_NOT_MOUNTED:
                    logWarn('Bail submit skipped: Stripe Elements is not defined', { ...data.value });
                    break;

                default:
                    logWarn('Something went wrong in stripe confirm payment');
            }
        });
};

// WATCHER
watch(
    open,
    async isOpen => {
        if (!isOpen) {
            if (isPaymentElementReady.value) {
                destroy();
                resetErrors();
            }

            return;
        }

        const setupIntent = await apiGetSetupIntent();
        const { clientSecret } = setupIntent;

        if (!setupIntent || !clientSecret) {
            onError();

            return;
        }

        // We need to await nextTick because modal must be open to mount stripe elements
        await nextTick();

        // Normal capture flow, only triggered if payment intent creation succeeds
        mountElements({ clientSecret });
        mountPayment({ id: '#add-payment-element', onFocus: resetErrors, onError });
    },
    { immediate: true }
);
</script>

<template>
    <UModal v-model:open="open" :fullscreen="fullscreen" :title="tt('paymentMethods.addModal.title')" v-bind="modalNoDescAriaFixupProps">
        <UButton color="neutral" variant="ghost" icon="lucide:plus" size="lg">
            <slot name="label">
                <span class="hidden md:inline">{{ label }}</span>
            </slot>
        </UButton>
        <template #body>
            <div class="w-full space-y-4">
                <div class="min-h-56 sm:min-h-36">
                    <div v-if="errors.length > 0" class="mb-2">
                        <BaseAlert v-for="(error, index) in errors" :key="index" type="error" :title="error" />
                    </div>
                    <USkeleton v-if="!isPaymentElementLoaderStart" class="h-56 w-full sm:h-36" />
                    <div id="add-payment-element-express" />
                    <div id="add-payment-element" @click="resetErrors" />
                </div>
            </div>
        </template>
        <template #footer>
            <UButton
                id="submit"
                size="xl"
                :loading="isConfirmingSetup"
                :disabled="isConfirmingSetup || errors.length > 0 || !isPaymentElementReady"
                color="primary"
                block
                :label="tt('paymentMethods.addModal.submit')"
                @click="onSubmit"
            />
        </template>
    </UModal>
</template>
